import React from 'react';
import cn from '@utils/cn';
import Text, { TextVariant } from '@ui/Text/Text';

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isMobile: boolean;
}

function Pagination({ currentPage, totalPages, onPageChange, isMobile }: PaginationProps) {
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const firstPage = 1;

  function getButton(currentSlideNumber: number) {
    const isSlideSelected = currentPage === currentSlideNumber;
    return (
      <div
        key={currentSlideNumber}
        className={cn(
          'text-body-foreground flex h-8 w-8 cursor-pointer items-center justify-center rounded',
          'hover:bg-input',
          {
            'bg-input cursor-default': isSlideSelected,
          },
        )}
        onClick={() => !isSlideSelected && onPageChange(currentSlideNumber)}
      >
        <Text content={String(currentSlideNumber)} variant={TextVariant.TextLabel} className="select-none" />
      </div>
    );
  }
  function getFirstPart() {
    if (currentPage === totalPages || currentPage === firstPage) {
      return pageNumbers
        .filter((number) => (!isMobile && number <= 3) || (isMobile && number <= 2))
        .map((number) => getButton(number));
    }
    if ((!isMobile && currentPage > 3) || (isMobile && currentPage > 2)) {
      return getButton(firstPage);
    }
    return null;
  }
  function getSecondPart() {
    const prevPages = isMobile ? currentPage - 1 : currentPage > 2 ? currentPage - 2 : 1;
    const nextPages = isMobile ? currentPage + 1 : currentPage < totalPages - 2 ? currentPage + 2 : totalPages;

    if (currentPage > firstPage && currentPage < totalPages) {
      return pageNumbers
        .filter((number) => number >= prevPages && number <= nextPages)
        .map((number) => getButton(number));
    }
    return null;
  }
  function getThirdPart() {
    if (currentPage === totalPages || currentPage === firstPage) {
      return pageNumbers
        .filter((number) => (!isMobile && number >= totalPages - 2) || (isMobile && number >= totalPages - 1))
        .map((number) => getButton(number));
    }
    if ((!isMobile && currentPage < totalPages - 2) || (isMobile && currentPage < totalPages - 1)) {
      return getButton(totalPages);
    }
    return null;
  }

  if ((!isMobile && totalPages <= 8) || (isMobile && totalPages <= 4)) {
    return pageNumbers.map((number) => getButton(number));
  }
  return (
    <>
      {getFirstPart()}
      {((!isMobile && currentPage > 4) || (isMobile && currentPage > 3)) && <div>...</div>}
      {getSecondPart()}
      {((!isMobile && currentPage < totalPages - 3) || (isMobile && currentPage < totalPages - 2)) && <div>...</div>}
      {getThirdPart()}
    </>
  );
}

export default Pagination;
